import React from "react";
import { MarkdownRenderer } from "./MarkdownRenderer";
import { ActionButtons } from "./ActionButtons";
import { GeneratedImages } from "./GeneratedImages";
import { GeneratedPosts } from "./GeneratedPosts";
import { GeneratedVideos } from "./GeneratedVideos";
import {
  MessageActions,
  ImageGenerationData,
  GeneratedPostsData,
  GeneratedVideosData,
  GeneratedVideo,
  DraftPost,
  UIAction,
} from "../types";
import { ActionClickContext } from "./ActionButtons";
import {
  CHAT_ASSISTANT_CONTENT_CLASS,
  CHAT_COLUMN_CLASS,
} from "../constants/chatLayout";
import { injectCitationLinks } from "../utils/injectCitationLinks";

interface StreamingMessageProps {
  content: string;
  /** Perplexity citation URLs, updated progressively during streaming (SGL-101) so
   * [1]/[2] markers become links as sources arrive instead of only at the end. */
  citations?: string[] | null;
  actions?: MessageActions | null;
  uiActions?: UIAction[] | null;
  generatedImages?: ImageGenerationData | null;
  generatedPosts?: GeneratedPostsData | null;
  generatedVideos?: GeneratedVideosData | null;
  onPublish?: (postData: MessageActions["post_data"]) => void;
  onSchedule?: (postData: MessageActions["post_data"]) => void;
  onSavePosts?: (selectedPosts: DraftPost[]) => void;
  onSaveVideos?: (selectedVideos: GeneratedVideo[]) => void;
  onActionClick?: (action: UIAction, context?: ActionClickContext) => void;
  isPublishing?: boolean;
  isScheduling?: boolean;
  isSavingPosts?: boolean;
  isSavingVideos?: boolean;
  isLoading?: boolean;
  /** Text is still arriving (streaming fade + caret). Defaults to isLoading; false
   *  while the turn waits on something else, so finished text isn't left faded. */
  isAnimating?: boolean;
  /** When true, do not render generated posts here (they are shown only in the right panel). */
  hideEmbeddedPosts?: boolean;
  /** When posts are hidden, call this to open the right panel with these posts. */
  onOpenPostsPanel?: (data: GeneratedPostsData) => void;
  onInsertImageForPost?: (tempId: string) => void | Promise<void>;
  /** Open the right-panel canvas for a specific post. */
  onOpenPostCanvas?: (post: DraftPost, allPosts: GeneratedPostsData) => void;
}

const StreamingMessageComponent: React.FC<StreamingMessageProps> = ({
  content,
  citations,
  actions,
  uiActions,
  generatedImages,
  generatedPosts,
  generatedVideos,
  onPublish,
  onSchedule,
  onSavePosts,
  onSaveVideos,
  onActionClick,
  isPublishing = false,
  isScheduling = false,
  isSavingPosts = false,
  isSavingVideos = false,
  isLoading = false,
  isAnimating = isLoading,
  hideEmbeddedPosts = false,
  onOpenPostsPanel,
  onInsertImageForPost,
  onOpenPostCanvas,
}) => {
  return (
    <div className={CHAT_COLUMN_CLASS}>
      <div className="flex min-w-0 w-full flex-col">
        <div className={CHAT_ASSISTANT_CONTENT_CLASS}>
          <MarkdownRenderer
            content={injectCitationLinks(content, citations ?? undefined)}
            isStreaming
            isAnimating={isAnimating}
          />
          {(actions || uiActions) && (onPublish || onSchedule || onActionClick) && (
            <ActionButtons
              actions={actions ?? undefined}
              uiActions={uiActions ?? undefined}
              onPublish={onPublish}
              onSchedule={onSchedule}
              onActionClick={
                onActionClick
                  ? (action) =>
                      onActionClick(action, {
                        generatedPosts: generatedPosts ?? undefined,
                      })
                  : undefined
              }
              isPublishing={isPublishing}
              isScheduling={isScheduling}
              isLoading={isLoading}
            />
          )}
        </div>
        {generatedImages && (
          <GeneratedImages
            imageUrls={generatedImages.urls}
            prompt={generatedImages.prompt}
            provider={generatedImages.provider}
            error={generatedImages.error}
          />
        )}
        {generatedPosts && hideEmbeddedPosts && onOpenPostsPanel && (
          <div className={CHAT_ASSISTANT_CONTENT_CLASS}>
            <button
              type="button"
              onClick={() => onOpenPostsPanel(generatedPosts)}
              className="mt-2 inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium text-theme-primary transition-colors hover:bg-theme-secondary/20 hover:text-theme-primary"
            >
              <span className="material-icons text-lg">open_in_new</span>
              View posts
            </button>
          </div>
        )}
        {generatedPosts && !hideEmbeddedPosts && onSavePosts && (
          <div className="mt-4 w-full min-w-0 max-w-full">
            <GeneratedPosts
              posts={generatedPosts.posts}
              onSave={onSavePosts}
              isSaving={isSavingPosts}
              onInsertImage={onInsertImageForPost}
              onOpenCanvas={
                onOpenPostCanvas && generatedPosts
                  ? (post) => onOpenPostCanvas(post, generatedPosts)
                  : undefined
              }
            />
          </div>
        )}
        {generatedVideos && onSaveVideos && (
          <div className={`mt-2 ${CHAT_ASSISTANT_CONTENT_CLASS}`}>
            <GeneratedVideos
              videos={generatedVideos.videos}
              onSave={onSaveVideos}
              isSaving={isSavingVideos}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export const StreamingMessage = React.memo(StreamingMessageComponent);
